import { ContentExtrasBar } from '@/components/content/ContentExtrasBar';
import { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  BookOpen,
  Lightbulb,
  RotateCcw,
  Trophy,
  Maximize2,
  Minimize2
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface InteractiveSlide {
  type: 'content' | 'quiz' | 'activity' | 'summary';
  title: string;
  content: string;
  image?: string;
  quiz?: {
    question: string;
    options: string[];
    correctIndex: number;
    explanation: string;
  };
  activity?: {
    instruction: string;
    placeholder: string;
  };
  tips?: string[];
}

interface InteractiveContentModalProps {
  isOpen: boolean;
  contentId?: string;
  onClose: () => void;
  lesson: {
    title: string;
    description: string;
    duration: string;
  } | null;
  htmlContent?: string;
  contentUrl?: string;
  onComplete: () => void;
  isCompleted: boolean;
}

// Sample interactive content - in production this would come from a CMS/database
const generateInteractiveContent = (lessonTitle: string): InteractiveSlide[] => {
  return [
    {
      type: 'content',
      title: 'Introducción',
      content: `Bienvenido a esta lección interactiva sobre "${lessonTitle}". En esta experiencia de aprendizaje, explorarás conceptos clave a través de contenido multimedia, actividades prácticas y evaluaciones que reforzarán tu comprensión.`,
      image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&h=400&fit=crop',
      tips: [
        'Toma notas mientras avanzas',
        'Completa todas las actividades',
        'Reflexiona sobre cómo aplicar estos conceptos'
      ]
    },
    {
      type: 'content',
      title: 'Conceptos Fundamentales',
      content: 'Los conceptos fundamentales que abordaremos en esta lección te proporcionarán una base sólida para comprender y aplicar las mejores prácticas en tu contexto profesional. Cada concepto está diseñado para ser práctico y aplicable inmediatamente.',
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=400&fit=crop',
    },
    {
      type: 'quiz',
      title: 'Verificación de Comprensión',
      content: 'Veamos qué tan bien has comprendido los conceptos hasta ahora.',
      quiz: {
        question: '¿Cuál es el primer paso recomendado al iniciar un nuevo proyecto de innovación?',
        options: [
          'Comenzar a implementar inmediatamente',
          'Definir claramente los objetivos y métricas de éxito',
          'Contratar más personal',
          'Comprar nuevas herramientas tecnológicas'
        ],
        correctIndex: 1,
        explanation: '¡Correcto! Definir objetivos claros y métricas de éxito es fundamental antes de iniciar cualquier proyecto de innovación. Esto proporciona dirección y permite medir el progreso.'
      }
    },
    {
      type: 'activity',
      title: 'Actividad Práctica',
      content: 'Ahora es tu turno de aplicar lo aprendido. Reflexiona sobre tu contexto actual y responde la siguiente actividad.',
      activity: {
        instruction: 'Describe un desafío actual en tu organización donde podrías aplicar estos conceptos. ¿Cómo los implementarías?',
        placeholder: 'Escribe tu reflexión aquí...'
      }
    },
    {
      type: 'content',
      title: 'Mejores Prácticas',
      content: 'Basándonos en la experiencia de múltiples organizaciones, estas son las mejores prácticas que deberías considerar: planificación estratégica, comunicación efectiva, medición continua y adaptación basada en resultados.',
      image: 'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=800&h=400&fit=crop',
      tips: [
        'Comunica constantemente con tu equipo',
        'Mide el progreso regularmente',
        'Adapta tu estrategia según los resultados'
      ]
    },
    {
      type: 'quiz',
      title: 'Evaluación Final',
      content: 'Una última pregunta para consolidar tu aprendizaje.',
      quiz: {
        question: '¿Qué característica es más importante en un líder de innovación?',
        options: [
          'Tener todas las respuestas',
          'Evitar tomar riesgos',
          'Fomentar la experimentación y aprender de los errores',
          'Trabajar de forma aislada'
        ],
        correctIndex: 2,
        explanation: '¡Excelente! Los mejores líderes de innovación fomentan la experimentación y ven los errores como oportunidades de aprendizaje, no como fracasos.'
      }
    },
    {
      type: 'summary',
      title: '¡Felicitaciones!',
      content: 'Has completado exitosamente esta lección interactiva. Los conceptos que has aprendido te ayudarán a liderar proyectos de innovación de manera más efectiva.',
      tips: [
        'Revisa tus notas regularmente',
        'Aplica un concepto esta semana',
        'Comparte lo aprendido con tu equipo'
      ]
    }
  ];
};

export function InteractiveContentModal({
  contentId,
  isOpen, 
  onClose, 
  lesson,
  htmlContent,
  contentUrl,
  onComplete,
  isCompleted: initialCompleted
}: InteractiveContentModalProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [activityResponse, setActivityResponse] = useState('');
  const [isCompleted, setIsCompleted] = useState(initialCompleted);
  const [completedSlides, setCompletedSlides] = useState<Set<number>>(new Set());
  const [fetchedHtml, setFetchedHtml] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const resolvedHtml = htmlContent || fetchedHtml;
  const hasCustomContent = !!(resolvedHtml || contentUrl);
  const slides = lesson && !hasCustomContent ? generateInteractiveContent(lesson.title) : [];
  const progress = slides.length > 0 ? ((currentSlide + 1) / slides.length) * 100 : 0;
  const currentSlideData = slides[currentSlide];

  useEffect(() => {
    if (contentUrl && !htmlContent) {
      setFetchedHtml(null);
      fetch(contentUrl)
        .then(res => res.text())
        .then(text => setFetchedHtml(text))
        .catch(() => setFetchedHtml(null));
    }
  }, [contentUrl, htmlContent]);

  useEffect(() => {
    if (isOpen) {
      setCurrentSlide(0);
      setSelectedAnswer(null);
      setShowExplanation(false);
      setActivityResponse('');
      setCompletedSlides(new Set());
      setIsFullscreen(false);
    }
  }, [isOpen]);

  const canProceed = () => {
    if (!currentSlideData) return false;
    
    if (currentSlideData.type === 'quiz') {
      return showExplanation;
    }
    if (currentSlideData.type === 'activity') {
      return activityResponse.trim().length >= 20;
    }
    return true;
  };

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCompletedSlides(prev => new Set([...prev, currentSlide]));
      setCurrentSlide(prev => prev + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
      setActivityResponse('');
    } else {
      if (!isCompleted) {
        setIsCompleted(true);
        onComplete();
      }
      onClose();
    }
  };

  const handlePrevious = () => {
    if (currentSlide > 0) {
      setCurrentSlide(prev => prev - 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    }
  };

  const handleAnswerSelect = (index: number) => {
    if (showExplanation) return;
    setSelectedAnswer(index);
    setShowExplanation(true);
  };

  const handleClose = () => {
    onClose();
  };

  const handleMarkComplete = () => {
    if (!isCompleted) {
      setIsCompleted(true);
      onComplete();
    }
    onClose();
  };

  if (!lesson) return null;

  // Custom HTML/URL content mode — render iframe
  if (hasCustomContent) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className={cn(
          "w-full p-0 bg-background border-border overflow-hidden",
          isFullscreen ? "max-w-[100vw] w-screen h-screen max-h-screen rounded-none" : "max-w-5xl max-h-[90vh]"
        )}>
        {contentId && <ContentExtrasBar contentId={contentId} description={lesson.description} />}
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border bg-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="font-semibold text-foreground">{lesson.title}</h2>
                <p className="text-sm text-muted-foreground">Contenido Interactivo • {lesson.duration}</p>
              </div>
            </div>
            <button
              onClick={() => setIsFullscreen(f => !f)}
              className="w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center transition-colors"
              title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4 text-muted-foreground" /> : <Maximize2 className="w-4 h-4 text-muted-foreground" />}
            </button>
          </div>

          {/* Iframe */}
          <div className="w-full" style={{ height: isFullscreen ? 'calc(100vh - 130px)' : '65vh' }}>
            {resolvedHtml ? (
              // SIN allow-same-origin: el HTML viene de content_bank/bucket. Con
              // allow-scripts, agregar allow-same-origin anula el sandbox y el
              // contenido podría robar la sesión de Supabase del usuario.
              <iframe
                srcDoc={resolvedHtml}
                sandbox="allow-scripts allow-popups allow-forms"
                className="w-full h-full border-0"
                title={lesson.title}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                Cargando contenido...
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end p-4 border-t border-border bg-card gap-3">
            {isCompleted ? (
              <span className="flex items-center gap-2 text-sm text-primary font-medium">
                <CheckCircle2 className="w-4 h-4" />
                Completado
              </span>
            ) : (
              <Button onClick={handleMarkComplete} className="gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Marcar como completado
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!currentSlideData) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl w-full p-0 bg-background border-border overflow-hidden max-h-[90vh]">
        {contentId && <ContentExtrasBar contentId={contentId} description={lesson.description} />}
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border bg-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="font-semibold text-foreground">{lesson.title}</h2>
              <p className="text-sm text-muted-foreground">Lección Interactiva • {lesson.duration}</p>
            </div>
          </div>
        </div>

        {/* Progress */}
        <div className="px-4 pt-3">
          <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
            <span>Progreso de la lección</span>
            <span>{currentSlide + 1} de {slides.length}</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Content Area */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Slide Type Badge */}
          <div className="flex items-center gap-2 mb-4">
            <span className={cn(
              "px-3 py-1 rounded-full text-xs font-medium",
              currentSlideData.type === 'content' && "bg-blue-500/10 text-blue-500",
              currentSlideData.type === 'quiz' && "bg-amber-500/10 text-amber-500",
              currentSlideData.type === 'activity' && "bg-green-500/10 text-green-500",
              currentSlideData.type === 'summary' && "bg-purple-500/10 text-purple-500"
            )}>
              {currentSlideData.type === 'content' && 'Contenido'}
              {currentSlideData.type === 'quiz' && 'Evaluación'}
              {currentSlideData.type === 'activity' && 'Actividad'}
              {currentSlideData.type === 'summary' && 'Resumen'}
            </span>
          </div>

          {/* Slide Title */}
          <h3 className="text-2xl font-bold text-foreground mb-4">{currentSlideData.title}</h3>

          {/* Image */}
          {currentSlideData.image && (
            <div className="mb-6 rounded-xl overflow-hidden">
              <img 
                src={currentSlideData.image} 
                alt={currentSlideData.title}
                className="w-full h-48 object-cover"
              />
            </div>
          )}

          {/* Content */}
          <p className="text-muted-foreground leading-relaxed mb-6">{currentSlideData.content}</p>

          {/* Quiz */}
          {currentSlideData.type === 'quiz' && currentSlideData.quiz && (
            <div className="space-y-4">
              <div className="bg-card rounded-xl p-5 border border-border">
                <p className="font-medium text-foreground mb-4">{currentSlideData.quiz.question}</p>
                <div className="space-y-3">
                  {currentSlideData.quiz.options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleAnswerSelect(index)}
                      disabled={showExplanation}
                      className={cn(
                        "w-full p-4 rounded-lg border text-left transition-all",
                        selectedAnswer === null && "border-border hover:border-primary hover:bg-primary/5",
                        selectedAnswer === index && showExplanation && index === currentSlideData.quiz!.correctIndex && "border-green-500 bg-green-500/10 text-green-700",
                        selectedAnswer === index && showExplanation && index !== currentSlideData.quiz!.correctIndex && "border-red-500 bg-red-500/10 text-red-700",
                        showExplanation && selectedAnswer !== index && index === currentSlideData.quiz!.correctIndex && "border-green-500 bg-green-500/5"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-6 h-6 rounded-full border-2 flex items-center justify-center text-sm font-medium",
                          selectedAnswer === index && showExplanation && index === currentSlideData.quiz!.correctIndex && "border-green-500 bg-green-500 text-white",
                          selectedAnswer === index && showExplanation && index !== currentSlideData.quiz!.correctIndex && "border-red-500 bg-red-500 text-white",
                          !showExplanation && "border-muted-foreground/30"
                        )}>
                          {String.fromCharCode(65 + index)}
                        </div>
                        <span>{option}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Explanation */}
              {showExplanation && (
                <div className={cn(
                  "p-4 rounded-lg animate-fade-in",
                  selectedAnswer === currentSlideData.quiz.correctIndex 
                    ? "bg-green-500/10 border border-green-500/30" 
                    : "bg-amber-500/10 border border-amber-500/30"
                )}>
                  <div className="flex items-start gap-3">
                    <Lightbulb className={cn(
                      "w-5 h-5 mt-0.5",
                      selectedAnswer === currentSlideData.quiz.correctIndex ? "text-green-500" : "text-amber-500"
                    )} />
                    <p className="text-sm">{currentSlideData.quiz.explanation}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Activity */}
          {currentSlideData.type === 'activity' && currentSlideData.activity && (
            <div className="space-y-4">
              <div className="bg-card rounded-xl p-5 border border-border">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <BookOpen className="w-4 h-4 text-green-500" />
                  </div>
                  <p className="font-medium text-foreground">{currentSlideData.activity.instruction}</p>
                </div>
                <textarea
                  value={activityResponse}
                  onChange={(e) => setActivityResponse(e.target.value)}
                  placeholder={currentSlideData.activity.placeholder}
                  className="w-full h-32 p-4 rounded-lg border border-border bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Mínimo 20 caracteres ({activityResponse.length}/20)
                </p>
              </div>
            </div>
          )}

          {/* Summary with Trophy */}
          {currentSlideData.type === 'summary' && (
            <div className="flex flex-col items-center text-center py-6">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mb-4 animate-bounce">
                <Trophy className="w-10 h-10 text-white" />
              </div>
              <p className="text-lg font-medium text-foreground mb-2">¡Lección Completada!</p>
            </div>
          )}

          {/* Tips */}
          {currentSlideData.tips && currentSlideData.tips.length > 0 && (
            <div className="mt-6 bg-primary/5 rounded-xl p-4 border border-primary/20">
              <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-primary" />
                Consejos
              </h4>
              <ul className="space-y-2">
                {currentSlideData.tips.map((tip, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between p-4 border-t border-border bg-card">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentSlide === 0}
            className="gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Anterior
          </Button>

          {/* Slide Indicators */}
          <div className="flex items-center gap-1.5">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  if (index <= Math.max(...Array.from(completedSlides), currentSlide)) {
                    setCurrentSlide(index);
                  }
                }}
                className={cn(
                  "w-2 h-2 rounded-full transition-all",
                  index === currentSlide && "w-6 bg-primary",
                  index !== currentSlide && completedSlides.has(index) && "bg-primary/50",
                  index !== currentSlide && !completedSlides.has(index) && "bg-muted"
                )}
              />
            ))}
          </div>

          <Button
            onClick={handleNext}
            disabled={!canProceed()}
            className="gap-2"
          >
            {currentSlide === slides.length - 1 ? (
              <>
                Finalizar
                <CheckCircle2 className="w-4 h-4" />
              </>
            ) : (
              <>
                Siguiente
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
